import { ArrowLeft, ArrowRight, Printer } from 'lucide-react';
import { ArticleCard } from '../components/ArticleCard';
import { articlesBySlug } from '../content/articles';
import type { Article } from '../content/articleTypes';
import { siteConfig } from '../siteConfig';

const formatDate = (value: string) =>
  new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

export const ArticlePage = ({ article }: { article: Article }) => {
  const related = article.relatedSlugs
    .map((slug) => articlesBySlug.get(slug))
    .filter((item): item is Article => Boolean(item));
  const nextTool = article.category === 'Motorisations'
    ? { href: '/outils/orientation-motorisation/', label: 'Tester mon usage', text: 'Comparez les familles de motorisation à partir de vos kilomètres et de votre recharge.' }
    : article.category === 'Financement' || article.slug === 'comparer-deux-voitures-occasion'
      ? { href: '/outils/comparateur-cout-global/', label: 'Comparer deux voitures', text: 'Ramenez deux véhicules à la même durée avec vos propres hypothèses de coût.' }
      : { href: '/outils/diagnostic-projet/', label: 'Diagnostiquer mon projet', text: 'Cinq questions pour identifier la prochaine étape utile de votre recherche.' };

  return (
    <main id="top" className="as-article-page">
      <nav className="as-breadcrumb" aria-label="Fil d’Ariane">
        <a href="/">Accueil</a><span aria-hidden="true">/</span>
        <a href="/conseils/">Conseils</a><span aria-hidden="true">/</span>
        <span aria-current="page">{article.category}</span>
      </nav>

      <article>
        <header className="as-article-hero">
          <div className="as-article-hero-copy">
            <p className="as-guide-meta">
              <span>{article.category}</span>
              <span>{article.readingTime} de lecture</span>
            </p>
            <h1>{article.title}</h1>
            <p className="as-article-intro">{article.intro}</p>
            <div className="as-article-byline">
              <span>Par <a href="/a-propos/" rel="author">Enzo Chevallier</a></span>
              <span>Mis à jour le <time dateTime={article.updatedAt}>{formatDate(article.updatedAt)}</time></span>
            </div>
          </div>
          <figure className="as-article-hero-image">
            <img
              src={article.image}
              alt={article.imageAlt}
              width="1200"
              height="630"
              loading="eager"
              {...{ fetchpriority: 'high' }}
              decoding="async"
            />
          </figure>
        </header>

        <section className="as-article-summary" aria-labelledby="article-summary-title" data-motion="split">
          <h2 id="article-summary-title">La réponse courte</h2>
          <ul>
            {article.takeaways.map((takeaway) => <li key={takeaway}>{takeaway}</li>)}
          </ul>
        </section>

        <div className="as-article-layout">
          <aside className="as-article-toc" data-motion="rise">
            <strong>Dans ce guide</strong>
            <ol>
              {article.sections.map((section) => (
                <li key={section.id}><a href={`#${section.id}`}>{section.title}</a></li>
              ))}
            </ol>
            <button type="button" onClick={() => window.print()}>
              <Printer size={14} aria-hidden="true" /> Imprimer ce guide
            </button>
          </aside>

          <div className="as-article-content">
            {article.sections.map((section) => (
              <section id={section.id} key={section.id}>
                <h2 data-motion="heading">{section.title}</h2>
                {section.content}
              </section>
            ))}

            <section className="as-article-next-step" aria-labelledby="article-next-step-title">
              <span>Passer du guide à votre cas</span>
              <h2 id="article-next-step-title">Un résultat immédiat, sans laisser votre e-mail.</h2>
              <p>{nextTool.text}</p>
              <a className="as-button as-button-lime" href={nextTool.href}>{nextTool.label} <ArrowRight size={15} aria-hidden="true" /></a>
            </section>

            <section className="as-article-service" aria-labelledby="article-service-title">
              <span>Conseil personnalisé</span>
              <h2 id="article-service-title">Vous préférez une shortlist faite pour votre situation ?</h2>
              <p>
                Je compare pour vous trois à cinq véhicules selon vos trajets, votre budget et leur
                coût global, sans commission d’une marque ou d’un vendeur. Offre pilote à 249 € TTC,
                dans la région d’Angers ou à distance. Retour sous 72 heures sur l’adéquation de
                l’offre à votre projet.
              </p>
              <a className="as-button as-button-dark" href="/#contact">Décrire mon projet <ArrowRight size={15} aria-hidden="true" /></a>
            </section>

            <section id="sources" className="as-article-sources">
              <h2 data-motion="heading">Sources et date de consultation</h2>
              <p>
                Sources consultées le {article.consultedAt || siteConfig.consultationDate}. Les prix, offres,
                restrictions et contenus externes peuvent évoluer après cette date.
              </p>
              <ol data-motion="stagger-lines">
                {article.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.title} <ArrowRight size={13} aria-hidden="true" />
                    </a>
                    <span>{source.publisher}</span>
                  </li>
                ))}
              </ol>
              <p className="as-source-disclosure">
                Aucun acteur cité ne rémunère Auto Sens. Les liens sont fournis pour permettre
                la vérification, pas comme recommandation commerciale.
              </p>
            </section>

            <section className="as-article-faq" aria-labelledby="article-faq-title">
              <h2 id="article-faq-title" data-motion="heading">Questions fréquentes</h2>
              <div className="as-article-faq-list" data-motion="stagger-lines">
                {article.faqs.map((faq) => (
                  <details key={faq.question}>
                    <summary>{faq.question}<span aria-hidden="true">+</span></summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="as-article-author" aria-label="À propos de l’auteur" data-motion="rise">
              <img src="/assets/autosens-mark.svg" alt="" width="48" height="48" />
              <div>
                <strong><a href="/a-propos/">Enzo Chevallier · Auto Sens</a></strong>
                <p>
                  Passionné d’automobile, je change de voiture à titre personnel chaque année depuis mon permis. Comme conseiller
                  automobile indépendant, j’aide à transformer usages, contraintes et budget en une shortlist argumentée.
                </p>
              </div>
            </section>
          </div>
        </div>
      </article>

      <section className="as-related-guides" aria-labelledby="related-guides-title">
        <div className="as-related-heading" data-motion="split">
          <div>
            <a href="/conseils/"><ArrowLeft size={15} aria-hidden="true" /> Tous les conseils</a>
            <h2 id="related-guides-title">Poursuivre la recherche</h2>
          </div>
          <p>Trois lectures reliées à cette décision.</p>
        </div>
        <div className="as-related-list">
          {related.map((item, index) => <ArticleCard key={item.slug} article={item} motionIndex={index % 2} />)}
        </div>
      </section>
    </main>
  );
};
